import { useEffect, useMemo, useRef, useState } from "react";

import { CustomButton } from "../../components/CustomButton";
import { AssignToUserRow } from "../../components/assets/AssignToUserRow";
import { CreateNewAssetRow } from "../../components/assets/CreateNewAssetRow";
import { CustomTable } from "../../components/table/CustomTable";
import {
  dispatchAllAssetsAction,
  dispatchAssetTypesAction,
  dispatchBulkAssignAssetsAction,
  dispatchBulkDeleteAssetsAction,
  dispatchBulkUnassignAssetsAction,
  dispatchCreateAssetAction,
  dispatchCreateAssetsAction,
} from "../../store/assets/actions";
import { RootState } from "../../store/store";
import { showErrorToast, showSuccessToast } from "../../utils/common";
import { useAppSelector } from "../../utils/hooks/store";
import { ISelectOption } from "../../common/interfaces";
import { useTranslation } from "react-i18next";

const AdminAssetsPage = () => {
  const { t } = useTranslation();
  const nameRef = useRef<HTMLInputElement>(null);
  const countRef = useRef<HTMLInputElement>(null);
  const checkDateRef = useRef<HTMLInputElement>(null);
  const [user, setUser] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [createMode, setCreateMode] = useState<boolean>(false);
  const [assignMode, setAssignMode] = useState<boolean>(false);
  const [assetsData, setAssetsData] = useState<any[]>([]);
  const { assets, types } = useAppSelector((state: RootState) => state.assets);

  const headers = [
    t("dashboard.assets.headers.id"),
    t("dashboard.assets.headers.uuid"),
    t("dashboard.assets.headers.name"),
    t("dashboard.assets.headers.type"),
    t("dashboard.assets.headers.createdAt"),
    t("dashboard.assets.headers.checkDate"),
    t("dashboard.assets.headers.user"),
  ];
  const createHeaders = [t("dashboard.assets.headers.name")];

  useEffect(() => {
    dispatchAllAssetsAction();
    dispatchAssetTypesAction();
  }, []);

  useEffect(() => setAssetsData(assets), [assets]);

  const onRowSelect = (id: string) => {
    if (selected.includes(id)) setSelected(selected.filter((selectedId) => selectedId !== id));
    else setSelected([...selected, id]);
  };

  const onBulkSelect = () => {
    if (selected.length === assets.length) setSelected([]);
    else setSelected(assets.map((asset) => asset.id));
  };

  const onBulkDelete = () => {
    if (selected)
      dispatchBulkDeleteAssetsAction(selected).then(() => {
        showSuccessToast("Assets deleted successfully!");
      });

    setSelected([]);
  };

  const onBulkUnassign = () => {
    if (selected)
      dispatchBulkUnassignAssetsAction(selected).then(() => {
        showSuccessToast("Assets unassigned successfully!");
        setSelected([]);
        dispatchAllAssetsAction();
      });
  };

  const onToggleAssignMode = () => setAssignMode(!assignMode);

  const onToggleCreateMode = () => setCreateMode(!createMode);

  const onNewAssetSubmit = () => {
    const name: string = nameRef.current?.value ?? "";
    const count: string = countRef.current?.value ?? "1";
    const checkDate: string = checkDateRef.current?.value ?? "";

    if (!name || !type || !count) return showErrorToast({ type: "required" });

    const numCount = +count;
    if (numCount < 1) return showErrorToast({ type: "invalid" });

    if (numCount === 1)
      dispatchCreateAssetAction({ name, typeId: type, checkDate })
        .then(() => showSuccessToast("Asset created successfully!"))
        .catch(() => {});
    else
      dispatchCreateAssetsAction({ name, typeId: type, count: numCount, checkDate })
        .then(() => {
          showSuccessToast("Assets created successfully!");

          dispatchAllAssetsAction();
        })
        .catch(() => {});

    onToggleCreateMode();
  };

  const onAssignSubmit = () => {
    if (!user) return showErrorToast({ type: "required" });

    dispatchBulkAssignAssetsAction({ userId: user, ids: selected })
      .then(() => {
        showSuccessToast("Assets assigned successfully!");
        onToggleAssignMode();
        setSelected([]);

        dispatchAllAssetsAction();
      })
      .catch(() => {});
  };

  const onSelectType = (id: number | string) => setType(String(id));

  const onSelectUser = (id: number | string) => setUser(String(id));

  const typeFilterOptions = useMemo(
    (): ISelectOption[] => types.map((type) => ({ value: type.name, label: type.name })),
    [types],
  );

  const onFilter = (value: string | number) => {
    if (value === "") return dispatchAllAssetsAction();

    const filtered = assets.filter((asset) => asset.type === value);
    setAssetsData(filtered);
  };

  return (
    <div className="user-requests-container">
      <CustomTable
        data={assetsData}
        className="mt-4"
        headers={headers}
        multiSelect={true}
        selected={selected}
        onSelect={onRowSelect}
        onBulkSelect={onBulkSelect}
        filtering={{
          filterLabel: t("dashboard.filterByType"),
          filterName: "type",
          filterOptions: typeFilterOptions,
          onFilter: onFilter,
        }}
      />
      {createMode && (
        <CreateNewAssetRow
          className="mb-4"
          inputRef={nameRef}
          countRef={countRef}
          headers={createHeaders}
          checkDateRef={checkDateRef}
          onSubmit={onNewAssetSubmit}
          onSelectType={onSelectType}
        />
      )}
      {assignMode && <AssignToUserRow className="mb-4" onSubmit={onAssignSubmit} onSelectUser={onSelectUser} />}
      <div className="d-flex justify-space-between">
        <div className="w-100 d-flex flex-column flex-lg-row gap-3">
          <CustomButton
            onClick={onToggleCreateMode}
            className="w-25 w-lg-100"
            styleType={createMode ? "secondary" : "success"}
            label={`${createMode ? t("dashboard.assets.discard") : t("dashboard.assets.create")} ${t("dashboard.assets.newAsset")}`}
          />
          <CustomButton
            styleType="primary"
            onClick={onToggleAssignMode}
            className="w-25 w-lg-100"
            label={t("dashboard.assets.assignToUser")}
            disabled={selected.length <= 0}
          />
          <CustomButton
            onClick={onBulkUnassign}
            styleType="secondary"
            className="w-25 w-lg-100"
            label={t("dashboard.assets.unassignSelected")}
            disabled={selected.length <= 0}
          />
          <CustomButton
            onClick={onBulkDelete}
            styleType="danger"
            className="w-25 w-lg-100"
            label={t("dashboard.assets.deleteSelected")}
            disabled={selected.length <= 0}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminAssetsPage;
